import { useState, type FormEvent } from "react";

// Número WhatsApp no formato internacional (sem + ou espaços)
const WHATSAPP_NUMBER = "557183822916";

// Opções do select "Área do Caso" — editáveis diretamente aqui
const AREAS_DO_CASO = [
  "Consultoria Jurídica",
  "Atuação Contenciosa",
  "Assessoria Jurídica",
  "Análise de Riscos Jurídicos",
  "Outros",
];

const contactInfo = [
  {
    icon: "ri-whatsapp-line",
    label: "WhatsApp",
    value: "(71) 8382-2916",
    href: `https://wa.me/${WHATSAPP_NUMBER}`,
    external: true,
  },
  {
    icon: "ri-mail-line",
    label: "E-mail",
    value: "georges@humbert.com.br",
    href: "mailto:georges@humbert.com.br",
    external: false,
  },
  {
    icon: "ri-map-pin-line",
    label: "Endereço",
    value:
      "Rua Sen. Theotônio Vilela, 225 — Sala 606\nEd. Centro Cidadela III\nParque Bela Vista — Salvador/BA\nCEP 40279-435",
    href: null,
    external: false,
  },
];

export default function ContatoSection() {
  const [charCount, setCharCount] = useState(0);
  const [sent, setSent] = useState(false);

  /**
   * Ao submeter:
   * 1. Captura os valores dos campos
   * 2. Monta a mensagem no formato exato solicitado
   * 3. Codifica com encodeURIComponent
   * 4. Abre o WhatsApp em nova aba com a mensagem pronta
   */
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    // Impede o comportamento padrão do formulário (recarregamento/envio HTTP)
    e.preventDefault();

    const form = e.currentTarget;

    // Captura os valores de cada campo
    const nome = (form.querySelector('[name="nome"]') as HTMLInputElement).value.trim();
    const email = (form.querySelector('[name="email"]') as HTMLInputElement).value.trim();
    const areaCaso = (form.querySelector('[name="area_caso"]') as HTMLSelectElement).value;
    const mensagem = (form.querySelector('[name="mensagem"]') as HTMLTextAreaElement).value.trim();

    // Valida limite de caracteres da mensagem
    if (mensagem.length > 500) return;

    // Monta a mensagem no formato exato solicitado
    const textoMensagem =
      `Nome Completo: ${nome} | E-mail: ${email} | Área do Caso: ${areaCaso} | Motivo do Contato: ${mensagem}`;

    // Codifica para URL
    const textoCodificado = encodeURIComponent(textoMensagem);

    // Abre o WhatsApp em nova aba com a mensagem pronta
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${textoCodificado}`, "_blank");

    // Exibe feedback de sucesso e reseta o formulário
    setSent(true);
    form.reset();
    setCharCount(0);
  };

  return (
    <section id="contato" className="bg-ivory py-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-gold-medium" />
            <span className="text-gold-medium text-xs font-semibold tracking-widest uppercase">
              Contato
            </span>
            <div className="w-8 h-px bg-gold-medium" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-petrol-dark mb-4">
            Entre em <span className="text-gold-medium">contato</span>
          </h2>
          <p className="text-petrol-dark/60 text-base max-w-md mx-auto">
            Preencha o formulário ou use os canais diretos abaixo. Retornaremos em breve.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Left: Form */}
          <div className="lg:col-span-3 bg-white rounded-lg border border-silver/30 p-8">
            {sent ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-petrol-dark/8 mx-auto mb-4">
                  <i className="ri-whatsapp-line text-petrol-dark text-3xl" />
                </div>
                <h3 className="text-petrol-dark font-semibold text-xl mb-2">
                  Redirecionando para o WhatsApp!
                </h3>
                <p className="text-petrol-dark/60 text-sm">
                  Sua mensagem foi preparada. Conclua o envio pelo WhatsApp.
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="mt-6 text-gold-medium text-sm font-semibold hover:text-gold-dark transition-colors cursor-pointer whitespace-nowrap"
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                id="contato-form"
                className="space-y-5"
              >
                {/* Nome Completo */}
                <div>
                  <label className="block text-petrol-dark text-xs font-semibold tracking-wide mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    required
                    placeholder="Seu nome completo"
                    className="w-full border border-silver/50 rounded-md px-4 py-3 text-sm text-petrol-dark placeholder-petrol-dark/30 focus:outline-none focus:border-gold-medium transition-colors bg-ivory"
                  />
                </div>

                {/* E-mail */}
                <div>
                  <label className="block text-petrol-dark text-xs font-semibold tracking-wide mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="seu@email.com"
                    className="w-full border border-silver/50 rounded-md px-4 py-3 text-sm text-petrol-dark placeholder-petrol-dark/30 focus:outline-none focus:border-gold-medium transition-colors bg-ivory"
                  />
                </div>

                {/* Área do Caso — opções dinâmicas e editáveis */}
                <div>
                  <label className="block text-petrol-dark text-xs font-semibold tracking-wide mb-2">
                    Área do Caso *
                  </label>
                  <select
                    name="area_caso"
                    required
                    defaultValue=""
                    className="w-full border border-silver/50 rounded-md px-4 py-3 text-sm text-petrol-dark focus:outline-none focus:border-gold-medium transition-colors bg-ivory cursor-pointer appearance-none"
                  >
                    {/* Opção padrão desabilitada */}
                    <option value="" disabled>
                      Selecione a área do seu caso
                    </option>

                    {/* Opções dinâmicas — edite o array AREAS_DO_CASO acima */}
                    {AREAS_DO_CASO.map((area) => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mensagem */}
                <div>
                  <label className="block text-petrol-dark text-xs font-semibold tracking-wide mb-2">
                    Mensagem *
                  </label>
                  <textarea
                    name="mensagem"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Descreva brevemente sua situação ou dúvida jurídica..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-silver/50 rounded-md px-4 py-3 text-sm text-petrol-dark placeholder-petrol-dark/30 focus:outline-none focus:border-gold-medium transition-colors bg-ivory resize-none"
                  />
                  <div
                    className={`text-right text-xs mt-1 ${
                      charCount > 450 ? "text-red-500" : "text-petrol-dark/40"
                    }`}
                  >
                    {charCount}/500
                  </div>
                </div>

                {/* Botão Enviar → abre WhatsApp */}
                <button
                  type="submit"
                  disabled={charCount > 500}
                  className="w-full flex items-center justify-center gap-2 bg-petrol-dark hover:bg-petrol-medium disabled:opacity-50 text-white font-semibold py-4 rounded-md transition-all duration-300 text-sm tracking-wide cursor-pointer whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-white text-base" />
                  </span>
                  Enviar via WhatsApp
                </button>

                <p className="text-petrol-dark/40 text-xs text-center">
                  Seus dados são tratados com total sigilo e confidencialidade.
                </p>
              </form>
            )}
          </div>

          {/* Right: Contact info + Map */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            {/* Contact Info */}
            <div className="bg-petrol-dark rounded-lg p-7">
              <h3 className="text-white font-semibold text-base mb-6">
                Canais de Atendimento
              </h3>
              <div className="flex flex-col gap-5">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-md bg-gold-light/10 shrink-0">
                      <i className={`${item.icon} text-gold-light text-base`} />
                    </div>
                    <div>
                      <div className="text-white/40 text-xs mb-0.5">{item.label}</div>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.external ? "_blank" : undefined}
                          rel={item.external ? "nofollow noopener noreferrer" : undefined}
                          className="text-white/85 text-sm hover:text-gold-light transition-colors cursor-pointer"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-white/85 text-sm whitespace-pre-line leading-relaxed">
                          {item.value}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Google Map */}
            <div className="rounded-lg overflow-hidden border border-silver/30 w-full h-52">
              <iframe
                title="Localização Georges Humbert Advocacia Salvador BA"
                src="https://maps.google.com/maps?q=Rua+Sen.+Theot%C3%B4nio+Vilela%2C+225%2C+Parque+Bela+Vista%2C+Salvador%2C+BA%2C+Brasil&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
